import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../common/Modal';
import ConfirmDialog from '../ui/ConfirmDialog';

const MAX_REASON_LENGTH = 2000;

type Props = {
  open: boolean;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
};

export default function RtiExtensionRequestModal({ open, busy = false, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setError('');
    setConfirmOpen(false);
  }, [open]);

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleSave = () => {
    const text = reason.trim();
    if (!text) {
      setError(t('tasks.details.communications.rtiExtension.reasonRequired'));
      return;
    }
    if (text.length > MAX_REASON_LENGTH) {
      setError(
        t('tasks.details.communications.rtiExtension.reasonTooLong', { max: MAX_REASON_LENGTH }),
      );
      return;
    }
    setError('');
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    onSubmit(reason.trim());
  };

  return (
    <>
      <Modal
        open={open}
        title={t('tasks.details.communications.rtiExtension.modalTitle')}
        onClose={handleClose}
        boxClassName="app-modal--md"
        portal
      >
        <label className="tcd-rti-ext-label" htmlFor="rti-extension-reason">
          {t('tasks.details.communications.rtiExtension.reasonLabel')}
        </label>
        <textarea
          id="rti-extension-reason"
          className="note-ta"
          rows={6}
          value={reason}
          disabled={busy}
          maxLength={MAX_REASON_LENGTH}
          placeholder={t('tasks.details.communications.rtiExtension.reasonPlaceholder')}
          onChange={(e) => {
            setReason(e.target.value);
            if (error) setError('');
          }}
        />
        <p className="tcd-comm-hint">
          {reason.length}/{MAX_REASON_LENGTH} {t('tasks.details.communications.characters')}
        </p>
        {error ? (
          <p className="tcd-error" role="alert" style={{ marginTop: 8 }}>
            {error}
          </p>
        ) : null}
        <div className="cn-footer app-modal__actions" style={{ marginTop: 16 }}>
          <button type="button" className="btn-cn2" disabled={busy} onClick={handleClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn-ca" disabled={busy || !reason.trim()} onClick={handleSave}>
            {busy ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirmOpen}
        title={t('tasks.details.communications.rtiExtension.confirmTitle')}
        message={t('tasks.details.communications.rtiExtension.confirmMessage')}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        confirmLabel={t('common.yes')}
        cancelLabel={t('common.no')}
      />
    </>
  );
}
